import { Button, Checkbox, Table, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { formatNumber } from '../utils/utility';
import { TableCustomV3 } from './TableCustomV3';

FilterTable.propTypes = {
  isLoadingFilterTable: PropTypes.bool.isRequired,
  filterDimensionAndMetric: PropTypes.object.isRequired,
  setFilterDimensionAndMetric: PropTypes.func,
  dataDimensions: PropTypes.array,
  dataMetrics: PropTypes.array,
  dataStatistics: PropTypes.any,
};

export default function FilterTable(props) {
  const {
    isLoadingFilterTable,
    filterDimensionAndMetric,
    setFilterDimensionAndMetric,
    dataDimensions,
    dataMetrics,
    dataStatistics,
  } = props;
  const [dimensionIds, setDimensionIds] = useState([]);
  const [metricIds, setMetricIds] = useState([]);
  const [sorter, setSorter] = useState({ columnKey: null, order: null });

  const { dataViewTable, dataExport, fixedColumns } = useMemo(() => {
    if (!dataStatistics || !dataStatistics?.data?.statistics?.[0]) {
      return { dataViewTable: [], fixedColumns: [] };
    }

    let tmp = [];

    dataStatistics?.data?.statistics.forEach((statistic, i) => {
      const value = statistic.reduce((acc, item) => {
        acc[item.name] = formatNumber(item.value);
        acc.type = item.type;
        acc.id = item.id;
        acc.key = i;
        return acc;
      }, {});
      tmp.push(value);
    });

    return { dataViewTable: tmp, fixedColumns: dataStatistics?.data?.total || [] };
  }, [dataStatistics]);

  function isSortable(type, id) {
    if (type === 'dimension' && id !== 1) return false;
    return true;
  }

  function formatSorterValue(value) {
    if (value === 'ASC') return 'ascend';
    if (value === 'DESC') return 'descend';
    if (value === 'ascend') return 'ASC';
    if (value === 'descend') return 'DESC';
    return null;
  }

  const { columns, colSpan } = useMemo(() => {
    if (!dataViewTable || !dataStatistics?.data?.statistics?.[0]) {
      return { columns: [], colSpan: 0 };
    }

    const columns = dataStatistics?.data?.statistics?.[0].map((item, index) => {
      const { name, type, id } = dataStatistics?.data?.statistics[0][index];
      return {
        title: item?.name || 'Name',
        dataIndex: item?.name,
        key: index,
        value: item?.value,
        sorter: isSortable(type, id),
        sortOrder: sorter.columnKey === name ? sorter.order : null,
      };
    });

    const colSpan = dataStatistics?.data?.statistics[0].filter((item) => item.type === 'dimension')?.length || 0;

    return { colSpan: colSpan, columns: columns };
  }, [dataStatistics, dataViewTable, sorter]);

  useEffect(() => {
    setDimensionIds(filterDimensionAndMetric?.dimension_ids || []);
    setMetricIds(filterDimensionAndMetric?.metric_ids || []);
    setSorter({
      columnKey:
        dataMetrics?.find((item) => item.column === filterDimensionAndMetric?.order_by_conditions?.[0]?.name)?.name ||
        dataDimensions?.find((item) => item.column === filterDimensionAndMetric?.order_by_conditions?.[0]?.name)
          ?.name ||
        null,
      order: formatSorterValue(filterDimensionAndMetric?.order_by_conditions?.[0]?.value),
    });
  }, [filterDimensionAndMetric]);

  const handleChangeDimensions = (dimensionId) => {
    const checkDimensionChecked = dimensionIds.find((item) => item === dimensionId);
    if (!checkDimensionChecked) {
      setDimensionIds([...dimensionIds, dimensionId]);
    } else {
      setDimensionIds(dimensionIds.filter((item) => item !== dimensionId));
    }
  };

  const handleChangeMetrics = (metricId) => {
    const checkMetricChecked = metricIds.find((item) => item === metricId);
    if (!checkMetricChecked) {
      setMetricIds([...metricIds, metricId]);
    } else {
      setMetricIds(metricIds.filter((item) => item !== metricId));
    }
  };

  return (
    <div className="w-full">
      <div className="flex flex-row justify-start">
        <div className="flex-1 overflow-auto">
          <TableCustomV3
            isLoading={isLoadingFilterTable}
            columns={columns}
            dataSource={dataViewTable}
            totalItem={dataStatistics?.totalItems}
            params={filterDimensionAndMetric}
            setParams={setFilterDimensionAndMetric}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={colSpan}>
                    <b>Tổng</b>
                  </Table.Summary.Cell>
                  {fixedColumns.map((item, index) => {
                    return (
                      <Table.Summary.Cell key={index} index={index + 1}>
                        {formatNumber(item.value)}
                      </Table.Summary.Cell>
                    );
                  })}
                </Table.Summary.Row>
              </Table.Summary>
            )}
            onChange={(pagination, filters, sorter, currentDataSource) => {
              if (sorter?.field) {
                console.log('sorter', sorter);
                setSorter({
                  columnKey: sorter?.field,
                  order: sorter?.order,
                });
                setFilterDimensionAndMetric({
                  ...filterDimensionAndMetric,
                  order_by_conditions: [
                    {
                      name:
                        dataMetrics?.find((item) => item.name === sorter?.field)?.column ||
                        dataDimensions?.find((item) => item.name === sorter?.field)?.column,
                      value: formatSorterValue(sorter?.order),
                    },
                  ],
                });
              } else {
                setSorter({ columnKey: null, order: null });
                setFilterDimensionAndMetric({
                  ...filterDimensionAndMetric,
                  order_by_conditions: [],
                });
              }
            }}
          />
        </div>

        <div className="flex flex-col w-fit px-[14px] py-[14px] bg-[#F0F1F380] rounded-md">
          <div className="flex flex-col justify-between gap-2">
            <Typography.Text>Dimensions</Typography.Text>
            <div className="flex flex-col gap-1 pl-3">
              {dataDimensions?.map((itemDimension, index) => {
                return (
                  <Checkbox
                    key={itemDimension?.id}
                    id={itemDimension?.id}
                    checked={dimensionIds?.find((item) => item === itemDimension?.id)}
                    onChange={() => handleChangeDimensions(itemDimension?.id)}
                  >
                    {itemDimension.name}
                  </Checkbox>
                );
              })}
            </div>

            <Typography.Text>Metrics</Typography.Text>

            <div className="flex flex-col gap-1 pl-3">
              {dataMetrics?.map((itemMetric, index) => {
                return (
                  <Checkbox
                    key={itemMetric?.id}
                    id={itemMetric?.id}
                    checked={metricIds?.find((item) => item === itemMetric?.id)}
                    onChange={() => handleChangeMetrics(itemMetric?.id)}
                  >
                    {itemMetric.name}
                  </Checkbox>
                );
              })}
            </div>

            <div className="pt-5 text-center">
              <Button
                type="primary"
                disabled={dimensionIds?.length === 0 || metricIds?.length === 0}
                onClick={() => {
                  setFilterDimensionAndMetric({
                    pageId: 1,
                    limit: 20,
                    dimension_ids: dimensionIds,
                    metric_ids: metricIds,
                    order_by_conditions: [],
                  });
                }}
              >
                Áp dụng
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
